import {
  Divider,
  Page,
  Spinner,
  ErrorMessages,
  IconButton,
  IconButtonType
} from "@whitespace/whitespace-ui";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { useStateMachine } from "little-state-machine";
import { useCallback, useEffect, useState } from "react";
import {
  Button, Card, Col, Row, Form
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { formatDateWithTime } from "helpers/DateUtils";
import { IsNonSiteUser } from "modules/CheckPermissions";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import {
  WorkOrderEndOfLifeStatusesProvider,
  WorkOrderEndOfLifeStatusesVehicle
} from "modules/WorkOrderStatusEnum";
import IWorkOrderViewModel from "interfaces/IWorkOrderViewModel";
import ICompleteWorkOrderRequest from "interfaces/Request/ICompleteWorkOrderRequest";
import { useForm } from "react-hook-form";
import CompleteWorkOrderBreadcrumb from "./CompleteWorkOrderBreadcrumb";
import "./CompleteWorkOrder.scss";

const CompleteWorkOrder = () => {
  const navigate = useNavigate();
  const {
    id, source, sourceType, sourceId
  } = useParams();
  const { state } = useStateMachine();
  const [completeWorkOrderButtonDisabled, setCompleteWorkOrderButtonDisabled] = useState<boolean>(true);

  const {
    isSending: completeWorkOrderIsSending,
    errors: completeWorkOrderErrors,
    statusCode: completeWorkOrderStatus,
    sendRequest: completeWorkOrderSendRequest
  } = useAmplifyREST();

  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [workOrder, setWorkOrder] = useState<IWorkOrderViewModel | undefined>(
    state.completeWorkOrderRequest
  );

  const [isSiteUserCompletingProviderWorkOrder, setIsSiteUsCompletingProviderWorkOrder] = useState<boolean>();
  const [isAuthorised, setIsAuthorised] = useState<boolean | undefined>();

  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    setValue,
    watch
  } = useForm<ICompleteWorkOrderRequest>({
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const watchFields = watch(["status"]);

  useEffect(() => {
    if (state && state.completeWorkOrderRequest) {
      setWorkOrder(state.completeWorkOrderRequest);
      const isSiteUserCompletingProviderWO = !IsNonSiteUser(state.userRole!) && !state.completeWorkOrderRequest.collectorIsVehicle;
      setIsSiteUsCompletingProviderWorkOrder(isSiteUserCompletingProviderWO);
      setIsAuthorised(IsNonSiteUser(state.userRole!) || isSiteUserCompletingProviderWO);

      if (isSiteUserCompletingProviderWO) {
        setValue("status", "Collected");
      }
    } else {
      navigate(RoutePathsEnum.Unauthorised);
    }
  }, [state, setWorkOrder]);

  useEffect(() => {
    if (isAuthorised === false) {
      navigate(RoutePathsEnum.Unauthorised);
    }
  }, [isAuthorised]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const isWorkOrderCompleted = (): boolean => {
    if (!workOrder) {
      return true;
    }

    if (workOrder.collectorIsVehicle) {
      return WorkOrderEndOfLifeStatusesVehicle.includes(workOrder.status!);
    }

    return WorkOrderEndOfLifeStatusesProvider.includes(workOrder.status!);
  };

  const handleCompleteWorkOrderClick = (
    request: ICompleteWorkOrderRequest
  ): void => {
    setShowExceptions(true);

    completeWorkOrderSendRequest(
      `workorders/${encodeURI(id!)}/complete`,
      "PUT",
      request
    );
  };

  useEffect(() => {
    if (completeWorkOrderStatus === 200) {
      switch (state.viewCompleteWorkOrderPageOrigination) {
        case RoutePathsEnum.Map:
          navigate(`${state.viewCompleteWorkOrderPageOrigination}/${source}/${sourceType}/${sourceId}`);
          break;
        case RoutePathsEnum.WorkOrders:
        case RoutePathsEnum.Containers:
        default:
          navigate(state.viewCompleteWorkOrderPageOrigination!);
          break;
      }
    }
  }, [completeWorkOrderStatus]);

  const canFormBeSubmitted = (): void => {
    setCompleteWorkOrderButtonDisabled(
      !(getValues("status") && getValues("status").length > 0)
    );
  };

  useEffect(() => {
    canFormBeSubmitted();
  }, [watchFields]);

  const navigateOnCancel = (): void => {
    switch (state.viewCompleteWorkOrderPageOrigination) {
      case RoutePathsEnum.Map:
        navigate(`${RoutePathsEnum.WorkOrderView}/${id}/${source}/${sourceType}/${sourceId}`);
        break;
      case RoutePathsEnum.WorkOrders:
      case RoutePathsEnum.Containers:
      default:
        navigate(`${RoutePathsEnum.WorkOrderView}/${id}`);
        break;
    }
  };

  return (
    <Page>
      {showExceptions && completeWorkOrderErrors && (
        <AmplifyError
          errors={completeWorkOrderErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      {isAuthorised && (
        <Card>
          <Card.Header>
            <h2>
              Complete Work Order
              <Spinner
                animation="grow"
                visible={completeWorkOrderIsSending}
                spinnerText="Loading"
                variant="info"
              />
            </h2>
          </Card.Header>
          <Form
            autoComplete="off"
            onSubmit={handleSubmit(handleCompleteWorkOrderClick)}
          >
            <Card.Body className="no-padding">
              <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
                <Col xs={12} md={6} className="d-flex">
                  {workOrder && <CompleteWorkOrderBreadcrumb />}
                </Col>
                <Col className="d-flex justify-content-end user-filter">
                  {!isWorkOrderCompleted() && (
                    <IconButton
                      iconType={IconButtonType.Save}
                      variant="primary"
                      className="right-margin-small"
                      disabled={
                        completeWorkOrderButtonDisabled
                      || completeWorkOrderIsSending
                      }
                      aria-disabled={
                        completeWorkOrderButtonDisabled
                      || completeWorkOrderIsSending
                      }
                      aria-label="Complete work order"
                      type="submit"
                      spin={completeWorkOrderIsSending}
                      data-testid="buttonCompleteWorkOrder"
                    >
                      Complete
                    </IconButton>
                  )}
                  <Button
                    variant="secondary"
                    onClick={navigateOnCancel}
                    data-testid="buttonCancel"
                    aria-label={`Cancel complete work order and back to ${state.viewCompleteWorkOrderPageOrigination}`}
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
              <Divider />
              {workOrder && (
                <section className="view-work-order pt-2">
                  <Row className="mt-1 ms-1">
                    <Col xs={12} md={8}>
                      <div className="control-pane">
                        <div className="control-section">
                          <div className="d-table w-100 horiz-form-label form-label">
                            <div
                              className="d-table-row"
                              data-testid="workOrderReference"
                            >
                              <div className="d-table-cell pb-2">Ref:</div>
                              <div className="d-table-cell pb-2">
                                {workOrder.workOrderReference}
                              </div>
                            </div>

                            <div
                              className="d-table-row"
                              data-testid="hwrcSiteName"
                            >
                              <div className="d-table-cell pb-2">Site:</div>
                              <div className="d-table-cell pb-2">{`${workOrder.hwrcSiteName} - ${workOrder.regionName}`}</div>
                            </div>
                            <div className="d-table-row" data-testid="container">
                              <div className="d-table-cell pb-2">
                                Container Type:
                              </div>
                              <div className="d-table-cell pb-2">
                                {`${workOrder.containerName} - ${workOrder.containerWasteType} - ${workOrder.containerSize}`}
                              </div>
                            </div>
                            <div
                              className="d-table-row"
                              data-testid="allocatedTo"
                            >
                              <div className="d-table-cell pb-2">
                                Allocated To:
                              </div>
                              <div className="d-table-cell pb-2">
                                {workOrder.collectorName}
                              </div>
                            </div>
                            <div className="d-table-row" data-testid="status">
                              <div className="d-table-cell pb-2">Status:</div>
                              <div className="d-table-cell pb-2">
                                {workOrder.status}
                              </div>
                            </div>
                            <div className="d-table-row" data-testid="dueBy">
                              <div className="d-table-cell pb-2">Due By:</div>
                              <div className="d-table-cell pb-2">
                                {formatDateWithTime(workOrder.slaEnd)}
                              </div>
                            </div>
                            <div
                              className="d-table-row"
                              data-testid="disposalSite"
                            >
                              <div className="d-table-cell pb-2">
                                Disposal Site:
                              </div>
                              <div className="d-table-cell pb-2">
                                {workOrder.disposalSiteName}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row className="mt-1 ms-1">
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="status">
                        <Form.Label id="statusLabel" className="horiz-form-label">
                          Status:
                        </Form.Label>
                        <Col>
                          <Form.Select
                            {...register("status", {
                              onChange: () => canFormBeSubmitted()
                            })}
                            data-testid="ddlStatus"
                            aria-required
                            aria-invalid={false}
                            aria-labelledby="statusLabel"
                            onChange={(e: any) => {
                              setValue("status", e.target.value!);
                            }}
                            defaultValue={getValues("status")}
                            disabled={isSiteUserCompletingProviderWorkOrder}
                          >
                            <option key="" value="">Please Select</option>

                            {!isSiteUserCompletingProviderWorkOrder && (<option key="Abandoned" value="Abandoned">Abandoned</option>)}

                            {state
                            && !state.completeWorkOrderRequest
                              ?.collectorIsVehicle && (<option key="Collected" value="Collected">Collected</option>
                            )}
                          </Form.Select>
                        </Col>
                        <ErrorMessages
                          fieldName="status"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                    <Row className="mb-2">
                      <Form.Group as={Row} controlId="reason">
                        <Form.Label id="reasonLabel" className="horiz-form-label">
                          Comments:
                        </Form.Label>
                        <Col>
                          <Form.Control
                            type="text"
                            role="textbox"
                            aria-required="false"
                            aria-invalid={false}
                            aria-labelledby="reasonLabel"
                            maxLength={50}
                            {...register("reason")}
                          />
                        </Col>
                        <ErrorMessages
                          fieldName="reason"
                          errors={errors}
                          validationFailures={null}
                        />
                      </Form.Group>
                    </Row>
                  </Row>
                </section>
              )}
            </Card.Body>
          </Form>
        </Card>
      )}
    </Page>

  );
};

export default CompleteWorkOrder;
